import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import './ListingPreview.css';

const ListingPreview = ({ 
  photos, 
  category, 
  productDetails, 
  pricing, 
  visibility, 
  location, 
  contact, 
  description,
  isEditMode = false 
}) => {
  const formatPrice = (price) => {
    if (!price && price !== 0) return 'Price not set';
    
    return new Intl.NumberFormat('fr-CM', {
      style: 'currency',
      currency: 'XAF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getCategoryLabel = (cat) => {
    const categories = {
      '1': 'Pièces Auto',
      '2': 'Pièces Moto',
      '3': 'Moteurs',
      '4': 'Carrosserie',
      'auto_parts': 'Auto Parts',
      'moto_parts': 'Moto Parts',
      'engines': 'Engines',
      'body_parts': 'Body Parts',
      'accessories': 'Accessories',
      'tools': 'Tools',
      'complete_vehicles': 'Complete Vehicles',
      'scrap_vehicles': 'Scrap Vehicles'
    };
    return categories[cat] || cat;
  };

  const getConditionBadge = (condition) => {
    const badges = {
      'Neuf': { label: 'Neuf', color: '#28a745', bgColor: 'rgba(40, 167, 69, 0.1)' },
      'Comme Neuf': { label: 'Comme Neuf', color: '#28a745', bgColor: 'rgba(40, 167, 69, 0.1)' },
      'Comme Neu': { label: 'Comme Neuf', color: '#28a745', bgColor: 'rgba(40, 167, 69, 0.1)' },
      'Bon État': { label: 'Bon État', color: '#007AFF', bgColor: 'rgba(0, 122, 255, 0.1)' },
      'État Correct': { label: 'État Correct', color: '#FF9500', bgColor: 'rgba(255, 149, 0, 0.1)' },
      'Mauvais État': { label: 'Mauvais État', color: '#DC2626', bgColor: 'rgba(220, 38, 38, 0.1)' },
      'Pour Pièces Uniquement': { label: 'Pour Pièces', color: '#666', bgColor: '#F5F5F5' }
    };
    return badges[condition] || { label: condition, color: '#666', bgColor: '#F5F5F5' };
  };

  const primaryPhoto = photos?.[0];
  const conditionBadge = productDetails?.condition ? getConditionBadge(productDetails.condition) : null;

  const getCityName = (cityValue) => {
    const cities = {
      'yaounde': 'Yaoundé',
      'douala': 'Douala',
      'bamenda': 'Bamenda',
      'bafoussam': 'Bafoussam',
      'garoua': 'Garoua',
      'maroua': 'Maroua',
      'ngaoundere': 'Ngaoundéré',
      'bertoua': 'Bertoua',
      'ebolowa': 'Ebolowa',
      'buea': 'Buea'
    };
    return cities[cityValue] || cityValue;
  };

  const getRegionName = (regionValue) => {
    const regions = {
      'centre': 'Centre',
      'littoral': 'Littoral',
      'ouest': 'Ouest',
      'nord_ouest': 'Nord-Ouest',
      'sud_ouest': 'Sud-Ouest',
      'adamaoua': 'Adamaoua',
      'nord': 'Nord',
      'extreme_nord': 'Extrême-Nord',
      'est': 'Est',
      'sud': 'Sud'
    };
    return regions[regionValue] || regionValue;
  };

  const currentDate = new Date().toLocaleDateString('fr-CM');

  return (
    <div className="listing-preview-container">
      <div className="listing-preview-content">
        {/* Preview Header */}
        <div className="preview-header">
          <Icon name="visibility" size={20} color="#007AFF" />
          <div className="header-text">
            <h1 className="header-title">
              {isEditMode ? 'Aperçu après modification' : 'Aperçu de l\'annonce'}
            </h1>
            <p className="header-subtitle">
              Comment les acheteurs verront votre annonce
            </p>
          </div>
        </div>

        {/* Mode Édition Info */}
        {isEditMode && (
          <div className="edit-mode-info">
            <Icon name="info" size={16} color="#007AFF" />
            <p className="edit-mode-info-text">
              Voici l'aperçu de votre annonce après modification. Tous les changements seront visibles immédiatement après la mise à jour.
            </p>
          </div>
        )}

        {/* Mobile Preview Card */}
        <div className="preview-card">
          {/* Image Section */}
          <div className="image-section">
            {primaryPhoto ? (
              <div className="image-container">
                <img
                  src={primaryPhoto.uri || primaryPhoto.url}
                  alt={description?.title || 'Image du produit'}
                  className="preview-image"
                />
                {photos.length > 1 && (
                  <div className="image-count-badge">
                    <span className="image-count-text">+{photos.length - 1} plus</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="placeholder-image">
                <Icon name="broken_image" size={32} color="#999" />
              </div>
            )}

           
          </div>

          {/* Content Section */}
          <div className="content-section">
            {/* Title & Category */}
            <div className="title-section">
              <h2 className="product-title">
                {description?.title || 'Titre du produit'}
              </h2>
              <span className="product-category">
                {getCategoryLabel(category)}
              </span>
            </div>

            {/* Price */}
            <div className="price-section">
              <div>
                <span className="product-price">
                  {pricing?.price ? formatPrice(parseInt(pricing.price)) : 'Prix non défini'}
                </span>
                {pricing?.negotiable && (
                  <span className="negotiable-label">Négociable</span>
                )}
              </div>
              {pricing?.acceptTrade && (
                <div className="trade-badge">
                  <span className="trade-badge-text">Échange OK</span>
                </div>
              )}
            </div>

            {/* Vehicle Details */}
            {productDetails?.brand && (
              <div className="vehicle-details">
                {productDetails?.brand && (
                  <div className="vehicle-detail">
                    <span className="detail-label">Marque:</span>
                    <span className="detail-value">{productDetails.brand}</span>
                  </div>
                )}
                {productDetails?.model && (
                  <div className="vehicle-detail">
                    <span className="detail-label">Modèle:</span>
                    <span className="detail-value">{productDetails.model}</span>
                  </div>
                )}
                {productDetails?.year && (
                  <div className="vehicle-detail">
                    <span className="detail-label">Année:</span>
                    <span className="detail-value">{productDetails.year}</span>
                  </div>
                )}
              </div>
            )}

            {/* Compatible Models */}
            {productDetails?.compatibleModels && productDetails.compatibleModels.length > 0 && (
              <div className="compatible-section">
                <span className="compatible-label">Compatible avec:</span>
                <div className="models-container">
                  {productDetails.compatibleModels.slice(0, 3).map((model, index) => (
                    <div key={index} className="model-badge">
                      <span className="model-badge-text">{model}</span>
                    </div>
                  ))}
                  {productDetails.compatibleModels.length > 3 && (
                    <span className="more-models">
                      +{productDetails.compatibleModels.length - 3} plus
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Location */}
            <div className="location-section">
              <Icon name="place" size={14} color="#666" />
              <span className="location-text">
                {location?.city 
                  ? `${getCityName(location.city)}, ${getRegionName(location.region)}`
                  : 'Localisation non définie'
                }
              </span>
            </div>

            {/* Description */}
            {description?.description && (
              <div className="description-section">
                <span className="description-label">Description:</span>
                <p className="description-text">
                  {description.description}
                </p>
              </div>
            )}

            {/* Contact Button */}
            <div className="contact-buttons">
              {contact?.whatsapp && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => {}}
                  leftIcon={<Icon name="chat" size={16} color="#FFF" />}
                  className="whatsapp-button"
                >
                  WhatsApp
                  {contact?.isVerified && (
                    <Icon name="verified" size={14} color="#fff" className="verified-icon" />
                  )}
                </Button>
              )}
            </div>

            {/* Seller Info */}
            <div className="seller-section">
              <div className="seller-info">
                <div className="seller-avatar">
                  <Icon name="person" size={12} color="#FFF" />
                </div>
                <span className="seller-name">Vendeur Particulier</span>
              </div>
              <span className="listing-date">Publié le {currentDate}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ListingPreview;